import { X } from 'lucide-react';
import {
  useId,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Mirrors the shape the backend's EmailStr accepts: one @, a dotted domain,
// and no whitespace or a second @ anywhere.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const splitEmails = (text: string) =>
  text
    .split(/[,\s]+/)
    .map((email) => email.trim().toLowerCase())
    .filter(Boolean);

const partitionEmails = (text: string) => {
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const email of splitEmails(text)) {
    (EMAIL_PATTERN.test(email) ? valid : invalid).push(email);
  }
  return { valid, invalid };
};

type AdditionalEmailsInputProps = {
  emails: string[];
  onChange: (emails: string[]) => void;
  label?: string;
};

export const AdditionalEmailsInput = ({
  emails,
  onChange,
  label = 'Additional emails',
}: AdditionalEmailsInputProps) => {
  // Unique, since a page can show this input more than once
  const inputId = useId();
  const hintId = useId();
  const [draft, setDraft] = useState('');
  const [invalidEmails, setInvalidEmails] = useState<string[]>([]);

  const addEmails = (text: string) => {
    const { valid, invalid } = partitionEmails(text);
    if (valid.length) onChange(Array.from(new Set([...emails, ...valid])));
    setInvalidEmails(invalid);
    setDraft(invalid.join(', '));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      addEmails(draft);
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    addEmails(`${draft} ${event.clipboardData.getData('text')}`);
  };

  return (
    <div className="grid gap-2 text-sm">
      <div className="grid gap-0.5">
        <label htmlFor={inputId}>{label}</label>
        <p id={hintId} className="text-xs text-muted-foreground">
          Press Enter or type a comma to add an email. Pasting a list adds every
          email in it.
        </p>
      </div>
      <div className="flex gap-2">
        <Input
          id={inputId}
          name="additional_email"
          type="email"
          aria-describedby={hintId}
          value={draft}
          aria-invalid={invalidEmails.length > 0 || undefined}
          onChange={(event) => {
            setDraft(event.target.value);
            setInvalidEmails([]);
          }}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder="ada@example.com"
        />
        <Button
          type="button"
          variant="outline"
          disabled={!draft.trim()}
          onClick={() => addEmails(draft)}
        >
          Add
        </Button>
      </div>
      {invalidEmails.length ? (
        <p role="alert" className="text-xs text-destructive">
          Not a valid email: {invalidEmails.join(', ')}
        </p>
      ) : null}
      {emails.length ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {emails.map((email) => (
            <Badge
              key={email}
              variant="secondary"
              className="gap-1 rounded-md py-1 pr-1 font-normal"
            >
              {email}
              <button
                type="button"
                aria-label={`Remove ${email}`}
                className="flex items-center rounded-sm p-0.5 hover:bg-muted"
                onClick={() =>
                  onChange(emails.filter((kept) => kept !== email))
                }
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
};
